/**
 * groupChatView.component.tsx
 * Sentriqs
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/
import * as React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";

import { Button } from "@mui/material";

import { AuthContext } from "../auth/auth.context";
import { GroupNavContext } from "./groupNav.context";

import { GroupMembership, GroupState, GroupType} from "../models/group";
import { PermissionLevel } from "../models/auth";
import { ContentMessage, NotifyUser } from "../models/message";
import { ReactionContentType } from "../models/reaction";

import { ErrorSnackBar } from "../components/errorSnackBar.component";
import { ChatInput } from "./chatInput";
import { FileDownloader } from "./fileDownloader.component";
import { GroupHistoryContainer } from "./groupHistoryContainer";
import { GroupsHistoryHeader } from "../headers/groupHistoryHeader.component";

import { reportError } from "../services/error.service";
import { groupServiceFactory } from "../services/group.service";

interface GroupChatViewComponentParams {
  groupID: string | undefined
  [key: string]: string | undefined
}

let groupEditingDraftTimeout: NodeJS.Timeout | null = null;

export function GroupChatViewComponent (): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const groupNavContext = React.useContext<GroupNavContext>(GroupNavContext);
  const { groupID } = useParams<GroupChatViewComponentParams>() as {groupID: string};
  const [accountID, setAccountID] = React.useState<string>(authContext.membership?.accountID || "");
  const navigate = useNavigate();
  const location = useLocation();
  const [isParticipating, setIsParticipating] = React.useState<boolean>(false);
  const [haveMembership, setHaveMembership] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [joining, setJoining] = React.useState(false);
  //const [showAddMemberModal, setShowAddMemberModal] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState("");
  const [curGroupMembers, setCurGroupMembers] = React.useState<GroupMembership[]>([]);
  const [fileData, setFileData] = React.useState<{fileID: string, keyID: string}>({fileID: "", keyID: ""});

  const canSendMessages = groupNavContext.currentGroup &&
    groupNavContext.can(PermissionLevel.WRITE, "messages");

  React.useEffect(function () {
    const memberships: Array<GroupMembership> = groupNavContext.groupMemberships;
    const directMessages: Array<GroupMembership> = groupNavContext.directMessages;
    if ((!memberships || memberships.length <= 0) && (!directMessages || directMessages.length <= 0)) {
      groupNavContext.setCurrentGroup(null);
      return;
    }

    if (!groupID) {
      const toGroupID = memberships.length > 0 ? memberships[0].groupID :
        directMessages[0].groupID;
      const url = `/group/${encodeURIComponent(toGroupID)}`;
      navigate(url, {replace: true});
      return;
    }

    const membership: GroupMembership | undefined = groupNavContext.groupMembershipMap[groupID];

    if (!membership) {
      if (authContext.membership && accountID !== authContext.membership.accountID) {
        setAccountID(authContext.membership.accountID);
      } else {
        console.error("No membership with groupID:", groupID);
        setErrMsg("Group does not exist or you are not a member");
      }
      setHaveMembership(false);
      navigate("/group/", {replace: true});
      return;
    }

    if (!accountID && authContext.membership) {
      setAccountID(authContext.membership.accountID);
    }

    const participating = membership.haveRatchetTree || false;
    setHaveMembership(true);
    setIsParticipating(participating);
    if (membership.groupID === groupNavContext.currentGroup?.groupID && curGroupMembers.length > 0) {
      return;
    }

    setLoading(true);
    groupNavContext.setCurrentGroup(membership);

    if (/huddle\/?$/.test(location.pathname) && !groupNavContext.huddleGroup) {
      console.log("should start huddle now");
      groupNavContext.doHuddle(membership.groupID);
    }

    groupServiceFactory().getGroupMembers(membership.accountID, membership.groupID)
    .then(results => {
      setCurGroupMembers(results);
      setLoading(false);
    }, err => {
      console.error("Error:", err);
      setErrMsg("Error fetching group state: " + err.message);
      setLoading(false);
    });

  }, [groupID, authContext.membership, groupNavContext.groupMemberships.length] );

  React.useEffect(function () {
    if (!groupNavContext.currentGroup) { return; }
    // if our membership is no longer in the list, redirect back
    const g = groupNavContext.groupMemberships.find(
      g => g.groupID === groupNavContext.currentGroup?.groupID) ||
      groupNavContext.directMessages.find(m => m.groupID === groupID);
    if (!g) {
      navigate("/group/", {replace: true});
    } else {
      setIsParticipating(g.haveRatchetTree || false);
    }
  }, [groupNavContext.groupMemberships, groupNavContext.directMessages])

  async function removeThisDeviceFromGroup (): Promise<void> {
    try {
      if (!groupNavContext.currentGroup) { throw new Error("No Group Selected!"); }
      if (!authContext.membership) { throw new Error("No current membership"); }

      const groupService = groupServiceFactory();
      await groupService.removeDeviceFromGroup(authContext.membership.accountID,
        authContext.membership.userID, groupNavContext.currentGroup.groupID,
        authContext.membership.userID);
    } catch (err) {
      console.error("Error removing this device from group");
      setErrMsg((err as Error).message);
    }
  }

  function getDraft(): Promise<string | null> {
    if (!groupNavContext.currentGroup) { return Promise.resolve(null); }
    return groupServiceFactory().getDraft(groupNavContext.currentGroup.groupID);
  }
  function saveDraft(draft: string): void {
    if (!groupNavContext.currentGroup) { return; }
    const accountID = groupNavContext.currentGroup.accountID; // save this in case we navigate away
    const groupID = groupNavContext.currentGroup.groupID;
    groupEditingDraftTimeout && clearTimeout(groupEditingDraftTimeout);
    groupEditingDraftTimeout = setTimeout(() => {
      groupEditingDraftTimeout = null;
      (draft ?
        groupServiceFactory().saveDraft(accountID, groupID, null, draft) :
        groupServiceFactory().deleteDraft(groupID, null))
      .then(null, err => setErrMsg(err.message));
    }, 2000);
  }

  async function doMessageSend(msg: ContentMessage, userIDsToNotify: Array<NotifyUser> | undefined) {
    try {
      if (!groupNavContext.currentGroup || !authContext.membership) {
        setErrMsg("Please, select a group");
        return;
      }
      /*if (userIDsToNotify) {
        const memberMap: {[id: string]: GroupMembership} = {};
        const membersToAdd: Array<Membership> = [];
        for (const gMember of curGroupMembers) {
          memberMap[gMember.userID] = gMember;
        }
        for (const userID of userIDsToNotify) {
          if (!memberMap[userID]) {
            membersToAdd.push(groupNavContext.acctMemberMap[userID]);
          }
        }
        if (membersToAdd.length > 0) {
          console.warn("Need to add members:", membersToAdd);
        }
      }*/
      const groupService = groupServiceFactory();
      await groupService.sendMessage(authContext.membership.accountID,
        groupNavContext.currentGroup.groupID,
        msg, userIDsToNotify);
      console.log("clearing draft timeout and deleting draft");
      groupEditingDraftTimeout && clearTimeout(groupEditingDraftTimeout);
      groupEditingDraftTimeout = null;
      await groupService.deleteDraft(groupNavContext.currentGroup.groupID, null);
    } catch (err) {
      setErrMsg((err as Error).message);
      reportError(err as Error, "sending message")
      throw err; // throw to chatInput for now
    }
  }

  async function doRemoveMessage(msgID: string, frameID: string | undefined, threadID: string | undefined) {
    try {
      if (!groupNavContext.currentGroup || !authContext.membership) {
        console.log("no current group");
        setErrMsg("Please, select a group");
        return;
      }
      const groupService = groupServiceFactory();
      if (threadID) {
        await groupService.removeReply(authContext.membership.accountID,
          groupNavContext.currentGroup.groupID,
          threadID,
          msgID, frameID);
      } else {
        await groupService.removeMessage(authContext.membership.accountID,
          groupNavContext.currentGroup.groupID,
          msgID, frameID);
      }
    } catch (err) {
      console.error("Error removing message:", err);
      setErrMsg((err as Error).message);
      // error not caught by children so we do not throw
    }
  }

  async function doRemoveMessageLocally(msgID: string, _frameID: string | undefined, threadID: string | undefined) {
    if (!groupNavContext.currentGroup || !authContext.membership) {
      console.log("no current group");
      return;
    }
    try {
      if (threadID) {
        await groupServiceFactory().removeReplyLocally(authContext.membership.accountID,
          groupNavContext.currentGroup.groupID, threadID, msgID);
      } else {
        await groupServiceFactory().removeMessageLocally(authContext.membership.accountID,
          groupNavContext.currentGroup.groupID, msgID);
      }
    } catch (err) {
      console.error("Error removing message locally:", err);
      setErrMsg((err as Error).message);
    } 
  }

  async function addReaction(msg: ContentMessage, contentID: string, contentType: ReactionContentType,
    content: string): Promise<void> {
    const {accountID, firstname, lastname} = authContext.membership || {accountID: "", firstname: "", lastname: ""};
    
    try {
      await groupServiceFactory().sendReaction(accountID, msg.groupID, msg._id, msg.threadID,
        contentID, contentType, content, firstname, lastname);
    } catch (err) {
      // error reporting? 
      console.error("Error adding reaction");
      throw err;  // throw to groupHistoryContainer
    }
  }

  async function removeReaction(msg: ContentMessage, reactionID: string, contentID: string, frameID: string | undefined) {
    if (!authContext.membership) { return; }
    try {
      await groupServiceFactory().sendRemoveReaction(
        authContext.membership.accountID, msg.groupID, frameID, msg._id,
        msg.threadID, reactionID, contentID);
    } catch (err) {
      // error reporting?
      console.error("Error removing reaction");
      throw err;  // throw to groupHistoryContainer
    }
  }

  async function doMarkGroupAsRead () {
    const groupService = groupServiceFactory();
    try {
      if (!groupNavContext.currentGroup || !groupNavContext.currentGroup?.unreadMessages) {
        return;
      }
      await groupService.markGroupAsRead(groupNavContext.currentGroup);
    } catch (err) {
      console.error("Failed marking group as read:", err);
      setErrMsg((err as Error).message);
    }
  }

  function doFileDownload(fileID: string, keyID: string) {
    setFileData({fileID: fileID, keyID: keyID});
  }

  async function doJoinGroup() {
    try {
      if (!groupNavContext.currentGroup) { return; }
      setJoining(true);
      await groupNavContext.addThisDeviceToGroup(groupNavContext.currentGroup.groupID)
    } catch (err) {
      console.error("Failed joining group:", err);
      setErrMsg((err as Error).message);
    } finally {
      setJoining(false);
    }
  }

  async function recoverGroup() {
    try {
      if (!groupNavContext.currentGroup) { return; }
      setJoining(true);
      await groupNavContext.recoverGroup(groupNavContext.currentGroup.groupID);
    } catch (err) {
      console.error("Failed recovering group:", err);
      setErrMsg((err as Error).message);
    } finally {
      setJoining(false);
    }
  }

  async function enableArchiving() {
    try {
      if (!groupNavContext.currentGroup) { return; }
      setJoining(true);
      await groupNavContext.enableArchiving(groupNavContext.currentGroup.groupID);
    } catch (err) {
      console.error("Failed enabling archiving:", err);
      setErrMsg((err as Error).message);
    } finally {
      setJoining(false);
    }
  }

  function renderBottomView(): React.ReactElement<any, any> {
    let title = "";
    let text = "";
    let btnText = "";
    let btnAction: (() => void) | undefined = undefined;
    let showChat = false;
    let showBtn = groupNavContext.can(PermissionLevel.WRITE, "members");

    const curGroup = groupNavContext.currentGroup;
    if (!curGroup || !groupID) { return <></>; }

    const isInactive = curGroup.groupstate && curGroup.groupstate !== GroupState.active;

    if (curGroup.needArchiving) {
      title = "Chat Disabled";
      text = "Archiving must be enabled in this group before you can resume sending messages.";
      btnText = "Enable Archiving";
      btnAction = enableArchiving;
    } else if (curGroup.haveRatchetTree && !curGroup.fullyWelcomed) {
      title = "Waiting for Secrets";
      text = "Another participating device must share group secrets with this device.";
      showBtn = false;
    } else if (curGroup.outOfSync) {
      title = "Out of Sync";
      text = "This device appears to be out of sync with other members.";
      btnText = "Try Recovery";
      btnAction = recoverGroup;
      showBtn = true;
    } else if (isInactive) {
      showBtn = false;
      if (curGroup.groupstate == GroupState.frozen) {
        if (curGroup.grouptype == GroupType.dm) {
          title = "Archived";
          text = "A member of this Direct Message has been removed from the group or from the workspace.";
        } else {
          title = "Disabled";
          text = "This group has been disabled.";
        }
      } else if (curGroup.groupstate == GroupState.deleted) {
        title = "Deleted";
        text = "This group or direct message is being or has been deleted.";
      } else {
        title = "Archived";
        text = "This group has been archived.";
      }
    } else if (isParticipating && canSendMessages) {
      showChat = true;
    } else if (!isParticipating && haveMembership) {
      title = "Not Participating";
      text = "This device is not participating in the group. Would you like to add it?";
      btnText = "Add This Device";
      showBtn = true;
      btnAction = doJoinGroup;
    } else {
      return <></>;
    }

    if (showChat) {
      return  <div className="attached-both-sides group-input-container">
        <ChatInput
          groupID={groupID}
          threadID=""
          myUserID={authContext?.membership?.userID}
          sendOnReturnKey={authContext.membership?.sendOnReturn || false}
          getDraft={getDraft}
          saveDraft={saveDraft}
          onSend={doMessageSend}
          onFocus={doMarkGroupAsRead}
          getMatchingMembers={groupNavContext.getMatchingMembers}
          onError={msg => setErrMsg(msg) } />
      </div>;
    }
    return <div className="attached-both-sides enable-archiving-container">
        <div className="join-group-container">
        <h2>{title}</h2>
        <p>{text}</p>
        { showBtn &&
          <Button variant="contained" color="primary" size="large"
              disableRipple disableTouchRipple
              disabled={joining}
              onClick={btnAction}>{btnText}</Button>}
      </div>
    </div>;
  }

  return <>
    <div className="group-history-container" aria-busy={loading} role="feed">
      <GroupsHistoryHeader
          loadingGroup={loading}
          className="attached-both-sides"
          group={groupNavContext.currentGroup}
          doRemoveThisDevice={removeThisDeviceFromGroup} />
      { haveMembership ?
        <GroupHistoryContainer
          scrollingElem={groupNavContext.mainScrollingElement()}
          loadingGroup={loading}
          groupID={groupID}
          myUserID={authContext.membership?.userID || ""}
          authContext={authContext}
          doRemoveMessage={doRemoveMessage}
          doRemoveMessageLocally={doRemoveMessageLocally}
          doThread={groupNavContext.setThread}
          doDownloadFile={doFileDownload}
          addReaction={addReaction}
          removeReaction={removeReaction}
          didScrollToBottom={doMarkGroupAsRead}
          onError={msg => setErrMsg(msg)} /> :
        <div></div>}
    </div>
    {/* downloader placed before input so it won't affect space-between */}
    <FileDownloader groupID={groupID} {...fileData}
      didTrigger={() => setFileData({fileID: "", keyID: ""})}
      onError={() => setFileData({fileID: "", keyID: ""})} />
    { renderBottomView() }
    <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("")} />
  </>;
}
